import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Brain, Zap, Target, TrendingUp, ChevronRight } from 'lucide-react';
import { useAuth } from '@/components/auth/AuthProvider';
import { NutritionActivityCorrelationService } from '@/services/NutritionActivityCorrelationService';
import { useNavigate } from 'react-router-dom';

export function NutritionCoachingTeaser() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: recommendations, isLoading } = useQuery({
    queryKey: ['nutrition-coaching-teaser', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const [upcoming, adaptive] = await Promise.all([
        NutritionActivityCorrelationService.getUpcomingWorkoutRecommendations(user.id),
        NutritionActivityCorrelationService.generateAdaptiveRecommendations(user.id)
      ]);
      return [...upcoming, ...adaptive].slice(0, 2); // Show top 2 recommendations
    },
    enabled: !!user,
    refetchInterval: 10 * 60 * 1000,
  });

  const { data: correlations } = useQuery({
    queryKey: ['performance-correlation-teaser', user?.id],
    queryFn: () => NutritionActivityCorrelationService.getPerformanceCorrelations(user!.id, 7),
    enabled: !!user,
    refetchInterval: 15 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card className="animate-fade-in">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="h-5 w-40 bg-muted rounded animate-pulse" />
            <Brain className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="h-16 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const getRecommendationIcon = (type: string) => {
    switch (type) {
      case 'pre_workout': return <Zap className="h-4 w-4 text-orange-500" />;
      case 'post_workout': return <TrendingUp className="h-4 w-4 text-blue-500" />;
      case 'recovery': return <Target className="h-4 w-4 text-green-500" />;
      case 'performance': return <Brain className="h-4 w-4 text-purple-500" />;
      default: return <Brain className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  // Calculate performance insight
  const performanceInsight = correlations && correlations.length > 0 
    ? correlations.filter(c => c.correlation === 'positive').length / correlations.length * 100
    : 0;

  return (
    <Card className="hover-scale cursor-pointer animate-fade-in" onClick={() => navigate('/nutrition')}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span>AI Nutrition Coach</span>
          <Brain className="h-4 w-4 text-purple-600" />
        </CardTitle>
        <CardDescription className="text-xs">
          Smart recommendations based on your training
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {correlations && correlations.length > 0 && (
          <div className="flex items-center justify-between p-2 bg-muted/50 rounded text-xs">
            <span>Performance correlation</span>
            <Badge variant={performanceInsight > 70 ? "default" : performanceInsight > 50 ? "secondary" : "outline"}>
              {performanceInsight.toFixed(0)}% positive
            </Badge>
          </div>
        )}

        {recommendations && recommendations.length > 0 ? (
          <div className="space-y-2">
            {recommendations.map((rec) => (
              <div key={rec.id} className="flex items-start gap-2 p-2 border rounded hover:bg-muted/50 transition-colors">
                <div className="flex-shrink-0 mt-0.5">
                  {getRecommendationIcon(rec.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium line-clamp-2 leading-relaxed">
                    {rec.message}
                  </p>
                  {rec.nutritionTarget && (
                    <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                      {rec.nutritionTarget.protein && (
                        <span>🥩 {rec.nutritionTarget.protein}g</span>
                      )}
                      {rec.nutritionTarget.carbs && (
                        <span>🌾 {rec.nutritionTarget.carbs}g</span>
                      )}
                    </div>
                  )}
                </div>
                <Badge variant={getPriorityColor(rec.priority)} className="text-xs">
                  {rec.priority}
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            <Brain className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-xs">Log meals and workouts for AI insights</p>
          </div>
        )}

        <div className="pt-2 border-t">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full text-xs flex items-center justify-center gap-1"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/nutrition');
            }}
          >
            View All Insights
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}